import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import RichTextContent from '../forms/RichTextContent';
import {
  COLLABORATION_CHANNELS,
  COLLABORATION_COMMENT_TYPES,
  COLLABORATION_VISIBILITY,
  COLLABORATION_FLAG_STATUS,
  CHAT_TAB_LABELS,
  CHAT_TAB_BANNERS,
  CHAT_MULTI_SUBMISSION_BANNER,
  FLAG_STATUS_LABELS,
  FLAG_TABS,
  FLAG_TAB_LABELS,
  FLAG_TAB_BANNERS,
  FLAG_EVIDENCE_REMOVED_BANNER,
  normalizeCollaborationVisibility,
  normalizeFlagStatus
} from '../../constants/submissionCollaboration';
import {
  resolveActiveFlagReasons,
  formatCommentReasonLabels,
  collectCommentReasons
} from '../../constants/workSampleFlags';
import { flagStatusAuditLines } from '../../utils/flagStatusActors';
import './AssignmentCollaborationPanel.css';

function formatAuthorName(comment) {
  const name = `${comment.first_name || ''} ${comment.last_name || ''}`.trim();
  if (name) return name;
  if (comment.email) return comment.email;
  return 'User';
}

function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
}

function commentVisibility(comment) {
  return normalizeCollaborationVisibility(comment?.metadata?.visibility) || COLLABORATION_VISIBILITY.PUBLIC;
}

function commentFlagType(comment) {
  return String(comment?.type || '').trim().toLowerCase();
}

function commentFlagStatus(comment) {
  return normalizeFlagStatus(comment?.metadata?.flag_status);
}

function isWorkSampleFlag(comment) {
  return commentFlagType(comment) === COLLABORATION_COMMENT_TYPES.WORK_SAMPLE_FLAG;
}

function sortNewestFirst(items) {
  return [...(items || [])].sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });
}

const ChatIcon = () => (
  <svg
    className="assignment-collab__icon"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const FlagIcon = () => (
  <svg
    className="assignment-collab__icon"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

const GlobeTabIcon = () => (
  <svg className="assignment-collab__tab-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const PeopleTabIcon = () => (
  <svg className="assignment-collab__tab-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const NotesTabIcon = () => (
  <svg className="assignment-collab__tab-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

function ChatTabIcon({ visibility }) {
  switch (visibility) {
    case COLLABORATION_VISIBILITY.INTERNAL:
      return <PeopleTabIcon />;
    case COLLABORATION_VISIBILITY.PRIVATE:
      return <NotesTabIcon />;
    default:
      return <GlobeTabIcon />;
  }
}

const GeneralFlagTabIcon = () => (
  <svg className="assignment-collab__tab-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

const WorkSampleFlagTabIcon = () => (
  <svg className="assignment-collab__tab-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <path d="M9 14l2 2 4-4" />
  </svg>
);

function FlagTabIcon({ tab }) {
  return tab === FLAG_TABS.WORK_SAMPLE ? <WorkSampleFlagTabIcon /> : <GeneralFlagTabIcon />;
}

function ChannelLoadingOverlay() {
  return (
    <div className="assignment-collab__loading-overlay" aria-busy="true" aria-live="polite">
      <span className="assignment-collab__loading-spinner" aria-hidden />
      <span className="assignment-collab__loading-text">Loading…</span>
    </div>
  );
}

const CHAT_TAB_ORDER = [
  COLLABORATION_VISIBILITY.PUBLIC,
  COLLABORATION_VISIBILITY.INTERNAL,
  COLLABORATION_VISIBILITY.PRIVATE
];

const AssignmentCollaborationPanel = ({
  submissionId = null,
  submissionCount = 0,
  assignmentRelationshipId = null,
  capabilities = null,
  currentUserId = null,
  fetchWithAuth
}) => {
  const caps = capabilities || {};
  const chatCaps = caps.chat || {};
  const flagCaps = caps.flags || {};
  const showChat = chatCaps.showButton;
  const canLoadFlags = flagCaps.canRead || flagCaps.canCreate;

  const chatTabsAvailable = useMemo(
    () =>
      CHAT_TAB_ORDER.filter((vis) => {
        const c = chatCaps[vis];
        return c && (c.canRead || c.canWrite);
      }),
    [chatCaps]
  );

  const [activeChannel, setActiveChannel] = useState(null);
  const [chatTab, setChatTab] = useState(chatTabsAvailable[0] || COLLABORATION_VISIBILITY.PUBLIC);
  const [chatItems, setChatItems] = useState([]);
  const [flagItems, setFlagItems] = useState([]);
  const [portfolioContext, setPortfolioContext] = useState(null);
  const [flagTab, setFlagTab] = useState(FLAG_TABS.GENERAL);
  const [wsReasons, setWsReasons] = useState([]);
  const [wsTitle, setWsTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [flagsHydrated, setFlagsHydrated] = useState(() => !canLoadFlags);
  const loadGenerationRef = useRef(0);
  const rootRef = useRef(null);

  const showFlagsButton =
    flagCaps.canCreate ||
    flagCaps.canCreateWorkSample ||
    flagItems.length > 0 ||
    (canLoadFlags && !flagsHydrated);

  const flagTabsAvailable = useMemo(() => {
    const tabs = [];
    if (flagCaps.canRead || flagCaps.canCreate) {
      tabs.push(FLAG_TABS.GENERAL);
    }
    if (
      portfolioContext?.showWorkSampleTab &&
      (flagCaps.canRead || flagCaps.canCreateWorkSample)
    ) {
      tabs.push(FLAG_TABS.WORK_SAMPLE);
    }
    return tabs;
  }, [flagCaps, portfolioContext?.showWorkSampleTab]);

  const generalFlags = useMemo(
    () => sortNewestFirst(flagItems.filter((item) => !isWorkSampleFlag(item))),
    [flagItems]
  );

  const workSampleFlags = useMemo(
    () => sortNewestFirst(flagItems.filter((item) => isWorkSampleFlag(item))),
    [flagItems]
  );

  const flagReasonOptions = useMemo(
    () => resolveActiveFlagReasons(portfolioContext?.flagReasons || null),
    [portfolioContext?.flagReasons]
  );

  const filteredFlagItems = flagTab === FLAG_TABS.WORK_SAMPLE ? workSampleFlags : generalFlags;

  const canCreateGeneralFlag = flagTab === FLAG_TABS.GENERAL && flagCaps.canCreate;
  const canCreateWorkSampleFlag =
    flagTab === FLAG_TABS.WORK_SAMPLE &&
    flagCaps.canCreateWorkSample &&
    portfolioContext?.canCreateWorkSampleFlag === true;

  const loadChannel = useCallback(
    async (channel) => {
      if (!fetchWithAuth || !assignmentRelationshipId) return [];
      const params = new URLSearchParams({
        assignment_relationship_id: String(assignmentRelationshipId),
        channel
      });
      if (submissionId) params.set('submission_id', String(submissionId));
      const res = await fetchWithAuth(`/api/comments/collaboration?${params.toString()}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Failed to load');
      }
      return { data: Array.isArray(json.data) ? json.data : [], portfolioContext: json.portfolioContext || null };
    },
    [assignmentRelationshipId, submissionId, fetchWithAuth]
  );

  const loadAll = useCallback(async () => {
    if (!assignmentRelationshipId || !fetchWithAuth) return;
    const generation = ++loadGenerationRef.current;
    setLoading(true);
    setLoadError('');
    try {
      const [chatResult, flagsResult] = await Promise.all([
        showChat ? loadChannel(COLLABORATION_CHANNELS.CHAT) : Promise.resolve({ data: [], portfolioContext: null }),
        canLoadFlags ? loadChannel(COLLABORATION_CHANNELS.FLAGS) : Promise.resolve({ data: [], portfolioContext: null })
      ]);
      if (generation !== loadGenerationRef.current) return;
      setChatItems(chatResult.data);
      setFlagItems(flagsResult.data);
      setPortfolioContext(flagsResult.portfolioContext || chatResult.portfolioContext || null);
    } catch (err) {
      if (generation !== loadGenerationRef.current) return;
      setLoadError(err.message || 'Failed to load collaboration');
    } finally {
      if (generation !== loadGenerationRef.current) return;
      if (canLoadFlags) {
        setFlagsHydrated(true);
      }
      setLoading(false);
    }
  }, [assignmentRelationshipId, fetchWithAuth, loadChannel, showChat, canLoadFlags]);

  useEffect(() => {
    setFlagsHydrated(!canLoadFlags);
  }, [canLoadFlags, assignmentRelationshipId]);

  useEffect(() => {
    setDraft('');
    setEditingId(null);
    setChatItems([]);
    setFlagItems([]);
    if (canLoadFlags) {
      setFlagsHydrated(false);
    }
    if (assignmentRelationshipId && (showChat || canLoadFlags)) {
      loadAll();
    }
  }, [submissionId, assignmentRelationshipId, showChat, canLoadFlags, loadAll]);

  useEffect(() => {
    if (
      activeChannel === COLLABORATION_CHANNELS.FLAGS &&
      flagsHydrated &&
      !flagCaps.canCreate &&
      !flagCaps.canCreateWorkSample &&
      flagItems.length === 0
    ) {
      setActiveChannel(null);
    }
  }, [activeChannel, flagsHydrated, flagCaps.canCreate, flagCaps.canCreateWorkSample, flagItems.length]);

  useEffect(() => {
    if (!flagTabsAvailable.includes(flagTab)) {
      setFlagTab(flagTabsAvailable[0] || FLAG_TABS.GENERAL);
    }
  }, [flagTabsAvailable, flagTab]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!activeChannel || !root) return undefined;

    const syncPanelGeometry = () => {
      const triggerRect = root.getBoundingClientRect();
      const viewer = root.closest('.profile-assignment-viewer');
      const header = viewer?.querySelector('.profile-assignment-viewer__header');
      const headerBottom = header?.getBoundingClientRect().bottom ?? triggerRect.bottom;
      const viewerRect = viewer?.getBoundingClientRect();

      root.style.setProperty('--collab-panel-top', `${headerBottom + 6}px`);
      root.style.setProperty(
        '--collab-panel-bottom',
        viewerRect ? `${Math.max(8, window.innerHeight - viewerRect.bottom + 8)}px` : '12px'
      );
      root.style.setProperty('--collab-panel-right', `${Math.max(8, window.innerWidth - triggerRect.right)}px`);
    };

    syncPanelGeometry();
    window.addEventListener('resize', syncPanelGeometry);
    window.addEventListener('scroll', syncPanelGeometry, true);
    return () => {
      window.removeEventListener('resize', syncPanelGeometry);
      window.removeEventListener('scroll', syncPanelGeometry, true);
      root.style.removeProperty('--collab-panel-top');
      root.style.removeProperty('--collab-panel-bottom');
      root.style.removeProperty('--collab-panel-right');
    };
  }, [activeChannel]);

  const counts = useMemo(
    () => ({
      chat: chatItems.length,
      flags: flagItems.filter((f) => commentFlagStatus(f) !== COLLABORATION_FLAG_STATUS.RESOLVED).length
    }),
    [chatItems, flagItems]
  );

  const filteredChatItems = useMemo(
    () =>
      sortNewestFirst(chatItems.filter((item) => commentVisibility(item) === chatTab)),
    [chatItems, chatTab]
  );

  const closePanel = () => {
    setActiveChannel(null);
    setPostError('');
    setEditingId(null);
    setDraft('');
    setWsReasons([]);
    setWsTitle('');
  };

  const toggleWsReason = (code) => {
    const key = String(code || '').trim();
    if (!key) return;
    setWsReasons((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  const renderFlagActions = (item, status) => (
    <>
      {flagCaps.canMarkAddressed && status === COLLABORATION_FLAG_STATUS.OPEN ? (
        <button
          type="button"
          className="assignment-collab__link-btn"
          onClick={() => handleFlagStatus(item.id, COLLABORATION_FLAG_STATUS.ADDRESSED)}
        >
          Mark addressed
        </button>
      ) : null}
      {flagCaps.canResolve && status !== COLLABORATION_FLAG_STATUS.RESOLVED ? (
        <button
          type="button"
          className="assignment-collab__link-btn"
          onClick={() => handleFlagStatus(item.id, COLLABORATION_FLAG_STATUS.RESOLVED)}
        >
          Resolve
        </button>
      ) : null}
      {(flagCaps.canReopen || flagCaps.canResolve) &&
      (status === COLLABORATION_FLAG_STATUS.RESOLVED ||
        status === COLLABORATION_FLAG_STATUS.ADDRESSED) ? (
        <button
          type="button"
          className="assignment-collab__link-btn"
          onClick={() => handleFlagStatus(item.id, COLLABORATION_FLAG_STATUS.OPEN)}
        >
          Reopen
        </button>
      ) : null}
    </>
  );

  const openChannel = (channel) => {
    setActiveChannel(channel);
    setPostError('');
    setEditingId(null);
    setDraft('');
    setWsReasons([]);
    setWsTitle('');
    if (channel === COLLABORATION_CHANNELS.CHAT) {
      setChatTab(chatTabsAvailable[0] || COLLABORATION_VISIBILITY.PUBLIC);
    }
    if (channel === COLLABORATION_CHANNELS.FLAGS) {
      setFlagTab(FLAG_TABS.GENERAL);
    }
    loadAll();
  };

  const canComposeChat = chatTabsAvailable.length && chatCaps[chatTab]?.canWrite;

  const handlePost = async () => {
    const bodyText = String(draft || '').trim();
    if (posting) return;
    if (activeChannel === COLLABORATION_CHANNELS.FLAGS && flagTab === FLAG_TABS.WORK_SAMPLE) {
      if (!wsReasons.length || !bodyText) return;
    } else if (!bodyText) {
      return;
    }
    setPosting(true);
    setPostError('');
    try {
      let payload;
      if (activeChannel === COLLABORATION_CHANNELS.CHAT) {
        payload = {
          type: COLLABORATION_COMMENT_TYPES.FORMS_CHAT,
          visibility: chatTab,
          body: bodyText
        };
      } else if (flagTab === FLAG_TABS.WORK_SAMPLE) {
        payload = {
          type: COLLABORATION_COMMENT_TYPES.WORK_SAMPLE_FLAG,
          visibility: COLLABORATION_VISIBILITY.INTERNAL,
          body: bodyText,
          reasons: wsReasons,
          portfolio_id: portfolioContext?.portfolioId,
          ...(wsTitle.trim() ? { title: wsTitle.trim() } : {})
        };
      } else {
        payload = {
          type: COLLABORATION_COMMENT_TYPES.FORMS_FLAG,
          visibility: COLLABORATION_VISIBILITY.INTERNAL,
          body: bodyText
        };
      }
      const res = await fetchWithAuth('/api/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          submission_id: submissionId || undefined,
          assignment_relationship_id: assignmentRelationshipId
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Failed to post');
      }
      setDraft('');
      setWsReasons([]);
      setWsTitle('');
      setEditingId(null);
      await loadAll();
    } catch (err) {
      setPostError(err.message || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const handleSaveEdit = async (commentId) => {
    const bodyText = String(draft || '').trim();
    if (!bodyText || posting) return;
    setPosting(true);
    setPostError('');
    try {
      const res = await fetchWithAuth(`/api/comments/${commentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body: bodyText,
          submission_id: submissionId || undefined,
          assignment_relationship_id: assignmentRelationshipId
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Failed to save');
      }
      setDraft('');
      setEditingId(null);
      await loadAll();
    } catch (err) {
      setPostError(err.message || 'Failed to save');
    } finally {
      setPosting(false);
    }
  };

  const handleFlagStatus = async (flagId, status) => {
    try {
      const res = await fetchWithAuth(`/api/comments/${flagId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flag_status: status,
          submission_id: submissionId || undefined,
          assignment_relationship_id: assignmentRelationshipId
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Failed to update flag');
      }
      await loadAll();
    } catch (err) {
      setPostError(err.message || 'Failed to update flag');
    }
  };

  if (!assignmentRelationshipId || (!showChat && !canLoadFlags)) {
    return null;
  }

  if (!showChat && !showFlagsButton && flagsHydrated) {
    return null;
  }

  const isOwn = (item) => currentUserId != null && String(item.created_by) === String(currentUserId);
  const showChatTabBanners =
    activeChannel === COLLABORATION_CHANNELS.CHAT && chatTabsAvailable.length > 1;
  const showMultiSubmissionBanner =
    activeChannel === COLLABORATION_CHANNELS.CHAT && submissionCount > 1;
  const chatTabBannerText = CHAT_TAB_BANNERS[chatTab] || '';
  const chatTabBannerExtra =
    chatTab === COLLABORATION_VISIBILITY.PRIVATE && chatCaps.private?.canViewAll
      ? ' Admins may audit all private notes.'
      : '';

  const composePlaceholder =
    activeChannel === COLLABORATION_CHANNELS.FLAGS
      ? 'Describe what needs attention…'
      : chatTab === COLLABORATION_VISIBILITY.PRIVATE
        ? 'Write a private note for yourself…'
        : chatTab === COLLABORATION_VISIBILITY.INTERNAL
          ? 'Message staff (internal — hidden from the assignee)…'
          : 'Message the assignee…';

  return (
    <div
      ref={rootRef}
      className={`assignment-collab${activeChannel ? ' assignment-collab--open' : ''}`}
    >
      <div className="assignment-collab__triggers">
        {showChat ? (
          <button
            type="button"
            className={`assignment-collab__trigger assignment-collab__trigger--icon${activeChannel === COLLABORATION_CHANNELS.CHAT ? ' is-active' : ''}`}
            onClick={() =>
              activeChannel === COLLABORATION_CHANNELS.CHAT ? closePanel() : openChannel(COLLABORATION_CHANNELS.CHAT)
            }
            title="Chat"
            aria-label="Chat"
          >
            <ChatIcon />
            {counts.chat > 0 ? <span className="assignment-collab__badge">{counts.chat}</span> : null}
          </button>
        ) : null}
        {showFlagsButton ? (
          <button
            type="button"
            className={`assignment-collab__trigger assignment-collab__trigger--icon assignment-collab__trigger--flag${activeChannel === COLLABORATION_CHANNELS.FLAGS ? ' is-active' : ''}`}
            onClick={() =>
              activeChannel === COLLABORATION_CHANNELS.FLAGS ? closePanel() : openChannel(COLLABORATION_CHANNELS.FLAGS)
            }
            title="Flags"
            aria-label="Flags"
          >
            <FlagIcon />
            {counts.flags > 0 ? (
              <span className="assignment-collab__badge assignment-collab__badge--flag">{counts.flags}</span>
            ) : null}
          </button>
        ) : null}
      </div>

      {activeChannel ? (
        <div className="assignment-collab__panel" role="region" aria-label="Assignment collaboration">
          <div className="assignment-collab__panel-head">
            <span className="assignment-collab__panel-title">
              {activeChannel === COLLABORATION_CHANNELS.CHAT ? 'Chat' : 'Flags'}
            </span>
            <button type="button" className="assignment-collab__panel-close" onClick={closePanel} aria-label="Close">
              ×
            </button>
          </div>

          <div className="assignment-collab__panel-body">
            {activeChannel === COLLABORATION_CHANNELS.CHAT ? (
              <div className="assignment-collab__channel-wrap">
                {chatTabsAvailable.length > 1 ? (
                  <div className="assignment-collab__tabs" role="tablist" aria-label="Chat visibility">
                    {chatTabsAvailable.map((vis) => (
                      <button
                        key={vis}
                        type="button"
                        role="tab"
                        aria-selected={chatTab === vis}
                        className={`assignment-collab__tab assignment-collab__tab--${vis}${chatTab === vis ? ' is-active' : ''}`}
                        onClick={() => {
                          setChatTab(vis);
                          setEditingId(null);
                          setDraft('');
                        }}
                      >
                        <ChatTabIcon visibility={vis} />
                        <span>{CHAT_TAB_LABELS[vis]}</span>
                      </button>
                    ))}
                  </div>
                ) : null}

                <div
                  className={`assignment-collab__channel assignment-collab__channel--${chatTab}${chatTabsAvailable.length > 1 ? ' assignment-collab__channel--has-tabs' : ''}`}
                  role="tabpanel"
                  aria-label={`${CHAT_TAB_LABELS[chatTab]} chat`}
                >
                  {chatTabsAvailable.length === 1 ? (
                    <div className="assignment-collab__channel-head">
                      <ChatTabIcon visibility={chatTab} />
                      <span>{CHAT_TAB_LABELS[chatTab]}</span>
                    </div>
                  ) : null}

                  {showChatTabBanners && chatTabBannerText ? (
                    <p
                      className={`assignment-collab__tab-banner assignment-collab__tab-banner--${chatTab}`}
                      role="note"
                    >
                      <span className="assignment-collab__tab-banner-icon" aria-hidden>
                        <ChatTabIcon visibility={chatTab} />
                      </span>
                      <span>
                        {chatTabBannerText}
                        {chatTabBannerExtra}
                      </span>
                    </p>
                  ) : null}

                  {showMultiSubmissionBanner ? (
                    <p className="assignment-collab__submission-banner" role="note">
                      {CHAT_MULTI_SUBMISSION_BANNER}
                    </p>
                  ) : null}

                  <div className="assignment-collab__scroll-area">
                    {loading ? <ChannelLoadingOverlay /> : null}
                    <div className="assignment-collab__list">
                      {loadError ? (
                        <p className="assignment-collab__status assignment-collab__status--error">{loadError}</p>
                      ) : !loading && filteredChatItems.length === 0 ? (
                        <p className="assignment-collab__status">No messages yet.</p>
                      ) : !loading ? (
                      filteredChatItems.map((item) => {
                        const canEditThis =
                          chatTab === COLLABORATION_VISIBILITY.PRIVATE &&
                          chatCaps.private?.canEdit &&
                          isOwn(item);
                        return (
                          <article key={item.id} className="assignment-collab__item">
                            <header className="assignment-collab__item-head">
                              <span className="assignment-collab__author">{formatAuthorName(item)}</span>
                              <span className="assignment-collab__meta">
                                <time dateTime={item.created_at}>{formatWhen(item.created_at)}</time>
                              </span>
                            </header>
                            {editingId === item.id ? (
                              <textarea
                                className="assignment-collab__input"
                                rows={3}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                              />
                            ) : (
                              <RichTextContent html={item.body} className="assignment-collab__body" />
                            )}
                            <div className="assignment-collab__item-actions">
                              {canEditThis && editingId !== item.id ? (
                                <button
                                  type="button"
                                  className="assignment-collab__link-btn"
                                  onClick={() => {
                                    setEditingId(item.id);
                                    setDraft(item.body || '');
                                  }}
                                >
                                  Edit
                                </button>
                              ) : null}
                              {canEditThis && editingId === item.id ? (
                                <>
                                  <button
                                    type="button"
                                    className="assignment-collab__link-btn"
                                    onClick={() => handleSaveEdit(item.id)}
                                    disabled={posting}
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="assignment-collab__link-btn"
                                    onClick={() => {
                                      setEditingId(null);
                                      setDraft('');
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </article>
                        );
                      })
                      ) : null}
                    </div>
                  </div>

                  {editingId == null && canComposeChat ? (
                    <div className="assignment-collab__compose">
                      <textarea
                        className="assignment-collab__input"
                        rows={3}
                        placeholder={composePlaceholder}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        disabled={posting || loading}
                      />
                      {postError ? (
                        <p className="assignment-collab__status assignment-collab__status--error">{postError}</p>
                      ) : null}
                      <button
                        type="button"
                        className="assignment-collab__post"
                        onClick={handlePost}
                        disabled={posting || loading || !String(draft).trim()}
                      >
                        {posting ? 'Posting…' : 'Post'}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="assignment-collab__channel-wrap">
                {flagTabsAvailable.length > 1 ? (
                  <div className="assignment-collab__tabs assignment-collab__tabs--flags" role="tablist" aria-label="Flag type">
                    {flagTabsAvailable.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        role="tab"
                        aria-selected={flagTab === tab}
                        className={`assignment-collab__tab assignment-collab__tab--flag-${tab}${flagTab === tab ? ' is-active' : ''}`}
                        onClick={() => {
                          setFlagTab(tab);
                          setDraft('');
                          setWsReasons([]);
                          setWsTitle('');
                          setPostError('');
                        }}
                      >
                        <FlagTabIcon tab={tab} />
                        <span>{FLAG_TAB_LABELS[tab]}</span>
                      </button>
                    ))}
                  </div>
                ) : null}

                <div
                  className={`assignment-collab__channel assignment-collab__channel--flags assignment-collab__channel--flag-${flagTab}${flagTabsAvailable.length > 1 ? ' assignment-collab__channel--has-tabs' : ''}`}
                  role="tabpanel"
                  aria-label={`${FLAG_TAB_LABELS[flagTab]} flags`}
                >
                  {flagTabsAvailable.length === 1 ? (
                    <div className="assignment-collab__channel-head">
                      <FlagTabIcon tab={flagTab} />
                      <span>{FLAG_TAB_LABELS[flagTab]}</span>
                    </div>
                  ) : null}

                  {FLAG_TAB_BANNERS[flagTab] ? (
                    <p className={`assignment-collab__tab-banner assignment-collab__tab-banner--flag-${flagTab}`} role="note">
                      <span className="assignment-collab__tab-banner-icon" aria-hidden>
                        <FlagTabIcon tab={flagTab} />
                      </span>
                      <span>{FLAG_TAB_BANNERS[flagTab]}</span>
                    </p>
                  ) : null}

                  {flagTab === FLAG_TABS.WORK_SAMPLE &&
                  portfolioContext?.showWorkSampleTab &&
                  !portfolioContext?.submissionIncludedInPortfolio ? (
                    <p className="assignment-collab__tab-banner assignment-collab__tab-banner--warning" role="note">
                      {FLAG_EVIDENCE_REMOVED_BANNER}
                    </p>
                  ) : null}

                  <div className="assignment-collab__scroll-area">
                    {loading ? <ChannelLoadingOverlay /> : null}
                    <div className="assignment-collab__list">
                      {loadError ? (
                        <p className="assignment-collab__status assignment-collab__status--error">{loadError}</p>
                      ) : !loading && filteredFlagItems.length === 0 ? (
                        <p className="assignment-collab__status">No flags yet.</p>
                      ) : !loading ? (
                      filteredFlagItems.map((item) => {
                        const status = commentFlagStatus(item);
                        const reasonCodes = collectCommentReasons(item.metadata);
                        const reasonLabels = formatCommentReasonLabels(
                          item.metadata,
                          portfolioContext?.flagReasons || flagReasonOptions
                        );
                        const auditLines = flagStatusAuditLines(item.status_actors);
                        return (
                          <article
                            key={item.id}
                            className={`assignment-collab__item${isWorkSampleFlag(item) ? ' assignment-collab__item--work-sample' : ''}`}
                          >
                            <header className="assignment-collab__item-head">
                              <span className="assignment-collab__author">{formatAuthorName(item)}</span>
                              <span className="assignment-collab__meta">
                                {reasonLabels.map((reasonLabel, reasonIndex) => (
                                  <span
                                    key={`${item.id}-${reasonCodes[reasonIndex] || reasonIndex}`}
                                    className="assignment-collab__chip assignment-collab__chip--reason"
                                  >
                                    {reasonLabel}
                                  </span>
                                ))}
                                <span className={`assignment-collab__chip assignment-collab__chip--${status}`}>
                                  {FLAG_STATUS_LABELS[status] || status}
                                </span>
                                <time dateTime={item.created_at}>{formatWhen(item.created_at)}</time>
                              </span>
                            </header>
                            {item.metadata?.title ? (
                              <p className="assignment-collab__flag-title">{item.metadata.title}</p>
                            ) : null}
                            <RichTextContent html={item.body} className="assignment-collab__body" />
                            {auditLines.length ? (
                              <ul className="assignment-collab__flag-audit">
                                {auditLines.map((line) => (
                                  <li key={line}>{line}</li>
                                ))}
                              </ul>
                            ) : null}
                            <div className="assignment-collab__item-actions">{renderFlagActions(item, status)}</div>
                          </article>
                        );
                      })
                      ) : null}
                    </div>
                  </div>

                  {editingId == null && (canCreateGeneralFlag || canCreateWorkSampleFlag) ? (
                    <div className="assignment-collab__compose">
                      {flagTab === FLAG_TABS.WORK_SAMPLE ? (
                        <>
                          <fieldset className="assignment-collab__reason-fieldset">
                            <legend className="assignment-collab__field-label">
                              Compliance reasons (select all that apply)
                            </legend>
                            <div className="assignment-collab__reason-grid">
                              {flagReasonOptions.map((opt) => (
                                <label key={opt.code} className="assignment-collab__reason-option">
                                  <input
                                    type="checkbox"
                                    checked={wsReasons.includes(opt.code)}
                                    onChange={() => toggleWsReason(opt.code)}
                                    disabled={posting || loading}
                                  />
                                  <span>{opt.label}</span>
                                </label>
                              ))}
                            </div>
                          </fieldset>
                          <label className="assignment-collab__field-label" htmlFor="collab-ws-title">
                            Short title (optional)
                          </label>
                          <input
                            id="collab-ws-title"
                            type="text"
                            className="assignment-collab__input assignment-collab__input--text"
                            placeholder="e.g. Missing feedback on attempt 2"
                            value={wsTitle}
                            onChange={(e) => setWsTitle(e.target.value)}
                            disabled={posting || loading}
                          />
                        </>
                      ) : null}
                      <textarea
                        className="assignment-collab__input"
                        rows={3}
                        placeholder={
                          flagTab === FLAG_TABS.WORK_SAMPLE
                            ? 'Describe the compliance issue for the VDM correction loop…'
                            : 'Describe what needs attention on this assignment…'
                        }
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        disabled={posting || loading}
                      />
                      {postError ? (
                        <p className="assignment-collab__status assignment-collab__status--error">{postError}</p>
                      ) : null}
                      <button
                        type="button"
                        className="assignment-collab__post assignment-collab__post--flag"
                        onClick={handlePost}
                        disabled={
                          posting ||
                          loading ||
                          !String(draft).trim() ||
                          (flagTab === FLAG_TABS.WORK_SAMPLE && !wsReasons.length)
                        }
                      >
                        {posting
                          ? 'Posting…'
                          : flagTab === FLAG_TABS.WORK_SAMPLE
                            ? 'Add Work Sample flag'
                            : 'Add flag'}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AssignmentCollaborationPanel;
